"use client";

import React from "react";

import { useResultsContext } from "@/components/common/ResultsSliderBase/ResultsContext";
import { ChevronLeft } from "@/components/icons/ChevronLeft";
import { ChevronRight } from "@/components/icons/ChevronRight";
import { NextArrowIcon } from "@/components/icons/NextArrowIcon";
import { PrevArrowIcon } from "@/components/icons/PrevArrowIcon";
import { cn } from "@/utils/general";

type Theme = "nova" | "wild";
type Variant = "small" | "large";

type ResultsArrowProps = {
  className?: string;
  variant?: Variant;
  theme?: Theme;
};

const themeSizeClasses: Record<Theme, Record<Variant, { button: string; icon: string }>> = {
  nova: {
    small: {
      button: "w-6 h-6",
      icon: "h-6 w-6",
    },
    large: {
      button: "w-6 h-6 xl:w-12 xl:h-12",
      icon: "h-6 w-6 xl:h-12 xl:w-12",
    },
  },
  wild: {
    small: {
      button: "w-8 h-8",
      icon: "h-3 w-3",
    },
    large: {
      button: "w-10 h-10 xl:w-14 xl:h-14",
      icon: "h-3 w-3 xl:h-4 xl:w-4",
    },
  },
};

const themeButtonClasses: Record<Theme, string> = {
  nova: "",
  wild: "bg-nova-base-white shadow-md rounded-full",
};

export function ResultsPrevArrow({
  className,
  variant = "small",
  theme = "nova",
}: ResultsArrowProps) {
  const { setAutoplay, resultSliderRef, sliderApi } = useResultsContext();

  const handleClick = () => {
    if (sliderApi) {
      sliderApi.scrollPrev();
      setAutoplay(false);
    } else if (resultSliderRef?.slickPrev) {
      resultSliderRef.slickPrev();
      setAutoplay(false);
    }
  };

  const sizes = themeSizeClasses[theme][variant];
  const themeClasses = themeButtonClasses[theme];

  const PrevIcon = theme === "wild" ? ChevronLeft : PrevArrowIcon;

  return (
    <button
      onClick={handleClick}
      className={cn("flex items-center justify-center", sizes.button, themeClasses, className)}
      aria-label="Previous"
    >
      <PrevIcon className={sizes.icon} />
    </button>
  );
}

export function ResultsNextArrow({
  className,
  variant = "small",
  theme = "nova",
}: ResultsArrowProps) {
  const { setAutoplay, resultSliderRef, sliderApi } = useResultsContext();

  const handleClick = () => {
    if (sliderApi) {
      sliderApi.scrollNext();
      setAutoplay(false);
    } else if (resultSliderRef?.slickNext) {
      resultSliderRef.slickNext();
      setAutoplay(false);
    }
  };

  const sizes = themeSizeClasses[theme][variant];
  const themeClasses = themeButtonClasses[theme];
  const NextIcon = theme === "wild" ? ChevronRight : NextArrowIcon;

  return (
    <button
      onClick={handleClick}
      className={cn("flex items-center justify-center", sizes.button, themeClasses, className)}
      aria-label="Next"
    >
      <NextIcon className={sizes.icon} />
    </button>
  );
}
